import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Button } from "antd";
import { StarFilled, ReloadOutlined } from "@ant-design/icons";
import { buildAllTagsFromPayload, defaultProfile } from "components/pages/talents/talentListNew/helper";
import mail from "../../../../assets/svg/mail.svg";
import close from "../../../../assets/svg/close.svg";
import shiftEdit from "../../../../assets/svg/shiftEdit.svg";
import { addToShortlisted, clearFilteredTiers, getRoleSearchObject, removeShortlisted, updateShortlisted, updateShortlistedStatus } from "store/actions/tiers";
import PopupModal from "./PopupModal";
import { useSearchFilterManager } from "components/pages/talents/talentListNew/filters/useSearchFilterManager";
import { getFilteredTalentsList } from "store/actions/talents";
import SearchComponent from "components/pages/talents/talentListNew/searchFilter";
import { getInviteTimeLabel, isScheduled } from "globalFunctions/tiers";
import { getEventShifts } from "store/actions/shifts";
import { useParams } from "react-router-dom";

const baseUrl = process.env.REACT_APP_API_URL;

const setTiersFilter = v =>
  sessionStorage.setItem("tiersFilter", JSON.stringify(v));

const getTiersTags = () => {
  try {
    const val = sessionStorage.getItem("tiersFilterTags");
    return val && val !== "undefined" ? JSON.parse(val) : [];
  } catch {
    return [];
  }
};

const getTiersFilter = () => {
  try {
    const val = sessionStorage.getItem("tiersFilter");
    return val && val !== "undefined" ? JSON.parse(val) : {};
  } catch {
    return {};
  }
};

const setTiersTags = v =>
  sessionStorage.setItem("tiersFilterTags", JSON.stringify(v));

const Shortlisted = ({
  shortlisted,
  filteredTiersData,
  triggerUpdateShortlisted,
  triggerRemoveShortlisted,
  triggerFilterTalentsList,
  displayTextEditor,
  selectedRoleUUID,
  shiftList,
  triggerClearTiers,
  triggerGetRoleSearchObject,
  eventRoles,
  skillCategorized,
  languageCategorized,
  triggerAddToShortlisted,
  triggerUpdateShortlistedStatus,
  handleRefresh,
  triggerGetEventShifts,
  loggedInUserId
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [modalVariant, setModalVariant] = useState("");
  const [onConfirmAction, setOnConfirmAction] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { id } = useParams();

  const hasFilteredResults = filteredTiersData?.length > 0;

  const shortlistedUUIDSet = React.useMemo(() => {
    return new Set(shortlisted.map(t => t.uuid));
  }, [shortlisted]);

  const filteredListToRender = React.useMemo(() => {
    return filteredTiersData.filter(
      item => !shortlistedUUIDSet.has(item.uuid)
    );
  }, [filteredTiersData, shortlistedUUIDSet]);

  const listToRender = hasFilteredResults ? filteredListToRender : [];

  useEffect(() => {
    const payload = {
      roleUUID: selectedRoleUUID,
      onSuccess: (response, talentList) => {
        const tags = buildAllTagsFromPayload(response, [], {
          eventRoles,
          skillCategorized,
          languageCategorized
        });
        setTiersFilter(response);
        setTiersTags(tags);
      },
      onError: () => {
        console.error('error on get role search object')
      }
    }
    triggerGetRoleSearchObject(payload)
  }, [selectedRoleUUID])

  const onSearchFilter = (payload, currPage = 1, pageSize = 50) => {
    const isEmptyPayload = !payload || Object.keys(payload).length === 0;

    if (isEmptyPayload) {
      triggerClearTiers();
      return;
    }

    triggerFilterTalentsList({
      filterPayload: { ...payload, number: currPage, size: pageSize, orderByRating: true },
      isShortlistedFilters: true,
      onSuccess: () => {
        console.log("fetch filter tiers talents list success");
      },
      onError: () => {
        console.error("fetch filter tiers talents list error");
      },
    });
  };

  const handleShortlisted = (e, item) => {
    e.stopPropagation();

    const payload = {
      data: item,
      selectedRoleUUID,
      onSuccess: (item) => {
        let timezone = ''
        let tzAbbr = ''
        for (const shift of shiftList) {
          timezone = shift.timezone;
          tzAbbr = shift.startTZ;
        }
        const outStandingJob = item.outstanding_jobs?.[0] || item.completed_jobs?.[0] || null;
        const fireAfter = outStandingJob?.fire_after;
        let inviteLabel = "";
        if (outStandingJob && outStandingJob?.uuid) {
          inviteLabel = getInviteTimeLabel({
            outStandingJob,
            timezone,
            tzAbbr,
            loggedInUserId
          });
        }
        const personWithRole = {
          ...item.person,
          roleUUID: item.uuid,
          inviteStatus: item.status,
          timezone: timezone,
          tzAbbr: tzAbbr,
          inviteQuotaFilled:
            parseInt(item.talent_needed) <= parseInt(item.stats?.approved),
          inviteOfferedAt: item.offered_at,
          waitlistOfferedAt: item.waitlisted_offered_at,
          outStandingJob,
          inviteTimeLabel: inviteLabel,
          isScheduled: isScheduled(fireAfter, timezone)
        };
        triggerAddToShortlisted(personWithRole);
      },
      onError: () => {
        console.error("Error on shortlisting talent");
      }
    };

    triggerUpdateShortlisted(payload);
  };

  const handleClose = (e, uuid) => {
    e.stopPropagation();
    const payload = {
      uuid
    }
    triggerRemoveShortlisted(payload);
  };

  const handleInvite = async (_, item, rolePersonUUID) => {
    await displayTextEditor(item, "invite", rolePersonUUID);
  };

  const handleAccept = async (e, item, rolePersonUUID) => {
    e.stopPropagation();

    if (item?.inviteQuotaFilled) {
      openModal(
        "fully staffed",
        item,
        async () => {
          await displayTextEditor(item, "approved", rolePersonUUID);
        }
      );
      return;
    }

    await displayTextEditor(item, "approved", rolePersonUUID);
  };

  const handleDeny = async (_, item, rolePersonUUID) => {
    await displayTextEditor(item, "denied", rolePersonUUID);
  };

  const handleBackup = async (_, item, rolePersonUUID) => {
    await displayTextEditor(item, "waitlist_offered", rolePersonUUID);
  };

  const getModalDetails = (talent) => {
    if (!selectedRoleUUID || !shiftList?.length) return null;

    for (const shift of shiftList) {
      const role = shift.roles.find(r => r.uuid === selectedRoleUUID);
      if (role) {
        const imageLink =
          talent.photo ||
          talent?.primary_photo_attribute?.value
            ? baseUrl + (talent.photo || talent.primary_photo_attribute.value)
            : defaultProfile;
      
      const name =
        talent.name ||
        `${talent.first_name || ""} ${talent.last_name || ""}`.trim();

      const rating = talent.rating || talent.company_rating || "Unrated";

        return {
          name: name,
          rating: rating,
          photo: imageLink,
          shift: {
            name: shift.name,
            from: new Date(shift.startAt).toLocaleString(),
            to: new Date(shift.endAt).toLocaleString(),
          },
          duties: role.duties || "",
        };
      }
    }
    return null;
  };

  const openModal = (variant, item, onConfirm) => {
    setModalVariant(variant);
    setSelectedTalent(getModalDetails(item));
    setOnConfirmAction(() => onConfirm);
    setShowModal(true);
  };

  const rawFilterManager = useSearchFilterManager({
    getFilter: getTiersFilter,
    setFilter: setTiersFilter,
    getTags: getTiersTags,
    setTags: setTiersTags,
    onFilter: onSearchFilter,
  });

  const filterManager = {
    ...rawFilterManager,

    removeFilter: (uuid) => {
      rawFilterManager.removeFilter(uuid);
      triggerClearTiers();
    },

    clearAll: () => {
      rawFilterManager.clearAll();
      triggerClearTiers();
    },
  };

  const searchText = rawFilterManager.talentFilter?.search_field || "";

  const updatePersonStatus = async (e, item, rolePersonUUID, status) => {
    e.stopPropagation();
    const payload = {
      "uuid": rolePersonUUID,
      "status": status
    }
    await triggerUpdateShortlistedStatus(payload)
  }

  const shouldShowPerson = (item) => {
    const actualPersonStatus = item?.status;

    const inviteStatus = item?.inviteStatus;

    return (
      actualPersonStatus === "available" ||
      (
        actualPersonStatus === "inactive" &&
        ["accepted", "approved", "waitlist_offered", "waitlisted"].includes(inviteStatus)
      )
    );
  };

  const onPopupPrimary = async () => {
    if (onConfirmAction) {
      await onConfirmAction();
    }

    setShowModal(false);
    setOnConfirmAction(null);
  };

  const shortlistScrollRef = React.useRef(null);
  const prevShortlistedLength = React.useRef(shortlisted.length);

  useEffect(() => {
    const wasAdded = shortlisted.length > prevShortlistedLength.current;
    prevShortlistedLength.current = shortlisted.length;

    if (wasAdded && shortlistScrollRef.current) {
      shortlistScrollRef.current.scrollTo({
        top: shortlistScrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [shortlisted]);

  const onRefresh = () => {
    setIsRefreshing(true);
    const payload = {
      id,
      onSuccess: () => {
        handleRefresh();
        setIsRefreshing(false);
      },
      onError: () => {
        console.error('Error on refresh');
        setIsRefreshing(false);
      }
    };
    triggerGetEventShifts(payload);
  };

  return (
    <>
      <div className="border border-cadetGray/50 rounded-lg bg-white mt-4 p-4 relative">
        <div className="flex justify-between items-center border-b border-cadetGray/50 pb-2">
          <h3 className="text-xl font-semibold">Shortlisted</h3>
          <div className="border border-cadetGray shadow-button pt-[6px] px-2 pb-1 rounded-md mr-[10px]">
            <ReloadOutlined
              onClick={onRefresh}
              spin={isRefreshing}
              style={{ strokeWidth: "60", stroke: "currentColor" }}
              className={`text-base cursor-pointer text-fontGray hover:text-secondary transition-colors ${isRefreshing ? "pointer-events-none opacity-50" : ""}`}
            />
          </div>
        </div>

        {isRefreshing && (
          <div className="absolute inset-0 bg-white/60 rounded-lg z-10 flex items-center justify-center">
            <ReloadOutlined
              spin
              style={{ strokeWidth: "40", stroke: "currentColor" }}
              className="text-2xl text-fontGray"
            />
          </div>
        )}

        <div
          ref={shortlistScrollRef}
          className="scrollbar max-h-[360px] overflow-y-scroll"
          onMouseEnter={e => e.currentTarget.classList.add("is-hovered")}
          onMouseLeave={e => e.currentTarget.classList.remove("is-hovered")}
        >
          {shortlisted
            .filter(shouldShowPerson)
            .map(item => (
              <TalentItem
                key={item.id}
                item={item}
                showCancelIcon
                handleInvite={handleInvite}
                handleClose={handleClose}
                handleAccept={handleAccept}
                handleDeny={handleDeny}
                handleBackup={handleBackup}
                handleShortlisted={handleShortlisted}
                isShortlisted
                updatePersonStatus={updatePersonStatus}
              />
            ))
          }
        </div>

        <SearchComponent filterManager={filterManager} isShortlisted />

        {(filteredTiersData.length > 0 && searchText) && (
          <h4 className="my-3 font-semibold text-base bg-catskill p-3">
            Results for{" "}
            <span className="text-secondary capitalize">
              {searchText}
            </span>
          </h4>
        )}
        {(filteredTiersData.length === 0 && searchText) && (
          <h4 className="my-3 font-semibold text-base bg-catskill p-3">
            No results for{" "}
            <span className="text-secondary capitalize">
              {searchText}
            </span>
          </h4>
        )}
        {(listToRender.length === 0 && !searchText) && (
          <h4 className="my-3 font-semibold text-base bg-catskill p-3">
            No results found
          </h4>
        )}

        <div
          className="scrollbar max-h-[360px] overflow-y-scroll"
          onMouseEnter={e => e.currentTarget.classList.add("is-hovered")}
          onMouseLeave={e => e.currentTarget.classList.remove("is-hovered")}
        >
          {listToRender
            .filter(shouldShowPerson)
            .map(item => (
              <TalentItem
                key={item.id}
                item={item}
                showCancelIcon
                handleInvite={handleInvite}
                handleClose={handleClose}
                handleAccept={handleAccept}
                handleDeny={handleDeny}
                handleBackup={handleBackup}
                handleShortlisted={handleShortlisted}
              />
            ))
          }
        </div>
      </div>

      <PopupModal
        open={showModal}
        variant={modalVariant}
        talent={selectedTalent}
        onPrimary={onPopupPrimary}
        onCancel={() => {
          setShowModal(false);
          setOnConfirmAction(null);
        }}
      />
    </>
  );
};

const mapStateToProps = state => ({
  shortlisted: state.tiers.shortlisted || [],
  filteredTiersData: state.tiers.filteredTiersData || [],
  eventRoles: state.talent.eventRoles,
  skillCategorized: state.talent.skillCategorized,
  languageCategorized: state.talent.languageCategorized,
  shiftList: state.shifts.shiftList,
  loggedInUserId: state.auth.loginDetails.person_uuid
});

const mapDispatchToProps = {
  triggerUpdateShortlisted: updateShortlisted,
  triggerRemoveShortlisted: removeShortlisted,
  triggerFilterTalentsList: getFilteredTalentsList,
  triggerClearTiers: clearFilteredTiers,
  triggerGetRoleSearchObject: getRoleSearchObject,
  triggerAddToShortlisted: addToShortlisted,
  triggerUpdateShortlistedStatus: updateShortlistedStatus,
  triggerGetEventShifts: getEventShifts
};

export default connect(mapStateToProps, mapDispatchToProps)(Shortlisted);

export const TalentItem = ({
  item,
  handleShortlisted,
  isShortlisted = false,
  handleInvite,
  handleClose,
  handleAccept,
  handleDeny,
  handleBackup,
  updatePersonStatus
}) => {
  const imageStyle =
    "w-[34px] min-w-[34px] max-w-[42px] h-[42px] rounded-[6px]";

  const imageLink =
    item.photo ||
    item?.primary_photo_attribute?.value
      ? baseUrl + (item.photo || item.primary_photo_attribute.value)
      : defaultProfile;

  const name =
    item.name ||
    `${item.first_name || ""} ${item.last_name || ""}`.trim();

  const city = item.city || item.primary_address_city;

  const state =
    item.state ||
    (item?.primary_address?.state_province
      ? `${item.primary_address.state_province.name}, ${item.primary_address.state_province.country?.name || ""}`
      : "");

  const rating = item.rating || item.company_rating || "Unrated";
  const rolePersonUUID = item?.roleUUID || "";
  const inviteStatus = item?.inviteStatus || "";
  const inviteQuotaFilled = item?.inviteQuotaFilled;
  const inviteLabel = item?.inviteTimeLabel || "";
  const isScheduled = item?.isScheduled || false;
  const hasLocation = state || city;

  return (
    <div className="flex items-center justify-between px-[5px] py-[5px] border-b border-cadetGray/50 hover:bg-gray-50 cursor-pointer">
      <div className="flex items-center gap-4 w-[55%]">
        <img
          className={imageStyle}
          src={imageLink}
          onError={(e) => (e.target.src = defaultProfile)}
          alt="profile"
        />

        <div
          className={`w-[30%] flex flex-col ${
            hasLocation ? "justify-start" : "justify-center"
          }`}
        >
          <p className="text-[15px] text-[#0A0C10] font-medium leading-normal p-0">
            {name}
          </p>

          <p className="text-[13px] text-[#A1AEC4] leading-normal p-0 min-h-[18px]">
            {state || ""}
          </p>

          <p className="text-[13px] text-[#A1AEC4] leading-normal p-0 min-h-[18px]">
            {city || ""}
          </p>
        </div>

        <div className="w-[10%] text-[13px]">
          <StarFilled className="text-darkMintGreen" /> {rating}
        </div>
      </div>

      {isShortlisted && inviteStatus ? (
        <div className="flex gap-3">
          {(inviteStatus === "approved") && 
            <span className="flex text-sm font-bold text-darkMintGreen items-center capitalize">
              Approved
            </span>
          }
          {(inviteStatus === "waitlisted") && 
            <span className="flex text-sm font-bold text-sunshade items-center capitalize">
              Waitlisted
            </span>
          }
          {(inviteStatus === "rejected") && 
            <span className="flex text-sm font-bold text-sunshade items-center capitalize">
              Unavailable
            </span>
          }
          {(inviteStatus === "denied" || inviteStatus === "removed") && 
            <Button
              className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
              onClick={(e) => updatePersonStatus(e, item, rolePersonUUID, "added")}
            >
              <span className="text-sm font-medium text-fontGray">
                Add back
              </span>
            </Button>
          }

          {(inviteStatus === "added") && 
            <Button
              className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
              onClick={(e) => handleInvite(e, item, rolePersonUUID)}
            >
              <img src={mail} alt="Invite talent" className="w-4 h-4" />
              <span className="text-sm font-medium text-fontGray">
                Invite
              </span>
            </Button>
          }

         {(inviteStatus === "offered" || inviteStatus === "waitlist_offered") && (
          <div className="flex gap-2">
            <span className="font-normal text-xs mt-2">{inviteLabel}</span>

            <Button
              className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
              onClick={(e) => handleInvite(e, item, rolePersonUUID)}
            >
              <img
                src={isScheduled ? shiftEdit : mail}
                alt="action"
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-fontGray">
                {inviteStatus === "waitlist_offered"
                  ? (isScheduled ? "Manage Backup" : "Resend Backup")
                  : (isScheduled ? "Manage Invite" : "Resend Invite")}
              </span>
            </Button>
          </div>
        )}

          {(inviteStatus === "accepted") &&
            <>
              <span className="font-normal text-xs mt-2 text-sunshade">Available</span>
              {
                inviteQuotaFilled &&
                  <Button
                    className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
                    onClick={(e) => handleBackup(e, item, rolePersonUUID, inviteStatus)}
                  >
                    Backup
                  </Button>
              }
              
              <Button
                className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
                onClick={(e) => handleAccept(e, item, rolePersonUUID, inviteStatus)}
              >
                Approve
              </Button>
              <Button
                className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
                onClick={(e) => handleDeny(e, item, rolePersonUUID, inviteStatus)}
              >
                Deny
              </Button>
            </>
          }

          <Button
            className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
            onClick={(e) => handleClose(e, rolePersonUUID)}
            icon={<img src={close} alt="Remove" className="w-4 h-4" />}
          />
        </div>
      ) : (
        <Button
          className="rounded-md border border-cadetGray shadow-button px-2.5 py-1.5"
          onClick={(e) => handleShortlisted(e, item)}
        >
          Shortlist
        </Button>
      )}
    </div>
  );
};
